import { useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePasswordChangeRequired } from "@/hooks/usePasswordChangeRequired";
import { useAccountStatus } from "@/hooks/useAccountStatus";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  skipPasswordCheck?: boolean;
}

export function ProtectedRoute({ children, skipPasswordCheck = false }: ProtectedRouteProps) {
  // REMOVE BEFORE MERGE: Preview bypass — allows ?preview=true to skip auth for design review
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get('preview') === 'true') return <>{children}</>;

  const { user, loading: authLoading, isInitialized } = useAuth();
  const { mustChangePassword, loading: passwordLoading } = usePasswordChangeRequired();
  const { isPaused, loading: statusLoading } = useAccountStatus();
  const location = useLocation();
  
  // Track if initial auth has completed - prevents remount on token refresh
  const hasInitiallyLoaded = useRef(false);

  // Track if status and password checks have completed at least once.
  // CRITICAL: After the first successful check, NEVER show the loading spinner again.
  // Showing the spinner replaces children with a <div>, which UNMOUNTS every protected
  // page (BrandGuide, VisionAssistant, etc.) and DESTROYS all their React state
  // (wizard step, active conversation, form data, scroll position, etc.).
  // This was the root cause of the tab-switch reset bug.
  const hasStatusLoaded = useRef(false);
  const hasPasswordLoaded = useRef(false);

  // Only show loading spinner on initial auth check, not on subsequent refreshes
  if (!isInitialized || (authLoading && !hasInitiallyLoaded.current)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Mark that initial load is complete
  hasInitiallyLoaded.current = true;

  // Only redirect to auth if we're certain there's no user (after initialization)
  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // Track when status and password checks complete for the first time
  if (!statusLoading) hasStatusLoaded.current = true;
  if (!passwordLoading) hasPasswordLoaded.current = true;

  // Only show loading spinner if NEITHER check has EVER completed.
  // After first completion, we never show the spinner again to prevent unmounting children.
  if (!hasStatusLoaded.current || !hasPasswordLoaded.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to account paused page if status is 'paused'
  if (isPaused && location.pathname !== "/account-paused") {
    return <Navigate to="/account-paused" replace />;
  }

  // Redirect to password change page if required (unless we're already there)
  if (!skipPasswordCheck && mustChangePassword && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}
